import type { DateTime } from 'luxon';
import { getEventTextScale } from '@/features/colors/lib/gridEventText.lib';
import { isExperienceEvent } from '@/features/events/lib/event.lib';
import type { PageExperience } from '@/features/experiences/types';
import type { PrintableEvent } from '@/features/printables/types';
import {
  getDisplayCategory,
  getEventFeed,
  isFeedAccessible,
} from '@/features/sources/lib/feed.lib';
import {
  BlockLayouts,
  type EventTextSizes,
  type ListExperienceEventInput,
} from '@/types/gql.generated';
import { MIN_EVENT_MINUTES } from './dayByCategory.constants';
import type {
  DayColumn,
  DayColumnData,
  DayPageData,
  PositionedEvent,
  TimedEvent,
} from './dayByCategory.types';

export const DAY_COLUMNS_LAYOUTS = [
  BlockLayouts.DayByCategory,
  BlockLayouts.DayByWho,
  BlockLayouts.DayByLabel,
  BlockLayouts.DayBySource,
] as const;

export type DayColumnsBy = (typeof DAY_COLUMNS_LAYOUTS)[number];

export const getMinEventMinutes = (eventTextSize?: EventTextSizes) =>
  Math.ceil(MIN_EVENT_MINUTES * getEventTextScale(eventTextSize));

export const isDayColumnsLayout = (
  layoutId: BlockLayouts | undefined
): layoutId is DayColumnsBy =>
  (DAY_COLUMNS_LAYOUTS as readonly (BlockLayouts | undefined)[]).includes(
    layoutId
  );

export const UNASSIGNED_COLUMN_ID = 'unassigned';

// Past this many columns, only the ones with events on the day are drawn.
export const MAX_COLUMNS_WITHOUT_EVENTS = 8;

export const DEFAULT_START_HOUR = 7;
export const DEFAULT_END_HOUR = 21;

const UNASSIGNED_LABELS: Record<DayColumnsBy, string> = {
  [BlockLayouts.DayByCategory]: 'No color',
  [BlockLayouts.DayByWho]: 'None',
  [BlockLayouts.DayByLabel]: 'No label',
  [BlockLayouts.DayBySource]: 'Other',
};

type ColumnSource = Pick<
  PageExperience,
  'id' | 'title' | 'categories' | 'labels' | 'whoLabels' | 'feeds'
>;

type Facets = Pick<
  ListExperienceEventInput,
  'categories' | 'labels' | 'whoLabels' | 'sources'
>;

const bySortOrder = <T extends { sortOrder: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.sortOrder - b.sortOrder);

const getDefinedColumns = (
  mode: DayColumnsBy,
  source: ColumnSource
): DayColumn[] => {
  switch (mode) {
    case BlockLayouts.DayByWho:
      return bySortOrder(source.whoLabels).map(({ id, text }) => ({
        id,
        label: text,
      }));
    case BlockLayouts.DayByLabel:
      return bySortOrder(source.labels).map(({ id, text }) => ({
        id,
        label: text,
      }));
    case BlockLayouts.DayBySource:
      return [
        { id: source.id, label: source.title },
        ...source.feeds
          .filter(isFeedAccessible)
          .map(({ id, title }) => ({ id, label: title })),
      ];
    case BlockLayouts.DayByCategory:
    default:
      return bySortOrder(source.categories).map((category) => ({
        id: category.id,
        label: category.text ?? '',
        category,
      }));
  }
};

const getFacetFilter = (
  mode: DayColumnsBy,
  facets: Facets | undefined
): string[] | null | undefined => {
  switch (mode) {
    case BlockLayouts.DayByWho:
      return facets?.whoLabels;
    case BlockLayouts.DayByLabel:
      return facets?.labels;
    case BlockLayouts.DayBySource:
      return facets?.sources;
    case BlockLayouts.DayByCategory:
    default:
      return facets?.categories;
  }
};

export const getEventColumnIds = (
  mode: DayColumnsBy,
  experience: PageExperience,
  source: ColumnSource,
  event: PrintableEvent
): string[] => {
  if (mode === BlockLayouts.DayByCategory) {
    const category = getDisplayCategory(experience, event);
    return category ? [category.id] : [];
  }
  if (!isExperienceEvent(event)) {
    return mode === BlockLayouts.DayBySource ? [source.id] : [];
  }
  switch (mode) {
    case BlockLayouts.DayByWho:
      return event.metadata.whoLabels.map(({ id }) => id);
    case BlockLayouts.DayByLabel:
      return event.metadata.labels.map(({ id }) => id);
    case BlockLayouts.DayBySource:
    default: {
      const feedId = getEventFeed(experience, event)?.id;
      if (feedId) {
        return [feedId];
      }
      return event.origin ? [] : [source.id];
    }
  }
};

// An event lands in every column it matches, so an event for two people shows
// under both. Lanes are packed per column, after that split.
export const packLanes = (
  events: TimedEvent[],
  minDurationMinutes = 0
): PositionedEvent[] => {
  const sorted = [...events].sort(
    (a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute
  );
  const packed: PositionedEvent[] = [];
  let cluster: PositionedEvent[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    for (const event of cluster) {
      packed.push({ ...event, laneCount: laneEnds.length });
    }
    cluster = [];
    laneEnds = [];
  };

  for (const event of sorted) {
    const visualEnd = Math.max(
      event.endMinute,
      event.startMinute + minDurationMinutes
    );
    if (event.startMinute >= clusterEnd) {
      flush();
      clusterEnd = -Infinity;
    }
    let lane = laneEnds.findIndex((end) => end <= event.startMinute);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(visualEnd);
    } else {
      laneEnds[lane] = visualEnd;
    }
    cluster.push({ ...event, lane, laneCount: 0 });
    clusterEnd = Math.max(clusterEnd, visualEnd);
  }
  flush();

  return packed;
};

type BuildArgs = {
  data: DayPageData;
  mode: DayColumnsBy;
  experience: PageExperience;
  source: ColumnSource;
  facets?: Facets;
  minDurationMinutes?: number;
};

export const buildDayColumns = ({
  data,
  mode,
  experience,
  source,
  facets,
  minDurationMinutes,
}: BuildArgs): DayColumnData[] => {
  const facetFilter = getFacetFilter(mode, facets);
  const defined = getDefinedColumns(mode, source).filter(
    ({ id }) => !facetFilter?.length || facetFilter.includes(id)
  );
  const definedIds = new Set(defined.map(({ id }) => id));

  const allDayByColumn = new Map<string, PrintableEvent[]>();
  const timedByColumn = new Map<string, TimedEvent[]>();
  const assign = <T>(map: Map<string, T[]>, ids: string[], item: T) => {
    const known = ids.filter((id) => definedIds.has(id));
    for (const id of known.length ? known : [UNASSIGNED_COLUMN_ID]) {
      map.set(id, [...(map.get(id) ?? []), item]);
    }
  };

  for (const event of data.allDayEvents) {
    assign(
      allDayByColumn,
      getEventColumnIds(mode, experience, source, event),
      event
    );
  }
  for (const timed of data.timedEvents) {
    assign(
      timedByColumn,
      getEventColumnIds(mode, experience, source, timed.event),
      timed
    );
  }

  const hasEvents = (id: string) =>
    allDayByColumn.has(id) || timedByColumn.has(id);

  // Unnamed colors only earn a column when something uses them, matching the Key.
  const candidates = defined.filter(
    (column) =>
      mode !== BlockLayouts.DayByCategory ||
      column.label ||
      hasEvents(column.id)
  );
  const visible =
    candidates.length > MAX_COLUMNS_WITHOUT_EVENTS
      ? candidates.filter(({ id }) => hasEvents(id))
      : candidates;

  const columns: DayColumn[] = hasEvents(UNASSIGNED_COLUMN_ID)
    ? [...visible, { id: UNASSIGNED_COLUMN_ID, label: UNASSIGNED_LABELS[mode] }]
    : visible;

  return columns.map((column) => ({
    column,
    allDayEvents: allDayByColumn.get(column.id) ?? [],
    timedEvents: packLanes(
      timedByColumn.get(column.id) ?? [],
      minDurationMinutes
    ),
  }));
};

export const getHourRange = (
  timedEvents: TimedEvent[],
  minDurationMinutes = 0
): { startHour: number; endHour: number; trackEndMinute: number } => {
  let startHour = DEFAULT_START_HOUR;
  let endHour = DEFAULT_END_HOUR;
  let latestVisualEnd = 0;
  for (const { startMinute, endMinute } of timedEvents) {
    const visualEnd = Math.max(endMinute, startMinute + minDurationMinutes);
    startHour = Math.min(startHour, Math.floor(startMinute / 60));
    endHour = Math.max(endHour, Math.ceil(visualEnd / 60));
    latestVisualEnd = Math.max(latestVisualEnd, visualEnd);
  }
  endHour = Math.min(24, Math.max(endHour, startHour + 1));
  return {
    startHour,
    endHour,
    trackEndMinute: Math.max(endHour * 60, latestVisualEnd),
  };
};

const SLOT_MINUTES = 30;

const columnClause = (mode: DayColumnsBy, column: DayColumn): string => {
  if (column.id === UNASSIGNED_COLUMN_ID || !column.label) {
    return '';
  }
  switch (mode) {
    case BlockLayouts.DayByWho:
      return ` for ${column.label}`;
    case BlockLayouts.DayByLabel:
      return ` labeled ${column.label}`;
    case BlockLayouts.DayByCategory:
      return ` in the ${column.label} color`;
    default:
      return '';
  }
};

export const buildCreatePrompt = (
  date: DateTime,
  minuteOfDay: number,
  mode: DayColumnsBy,
  column: DayColumn
): string => {
  const lastSlot = 24 * 60 - SLOT_MINUTES;
  const slot =
    Math.floor(Math.min(Math.max(0, minuteOfDay), lastSlot) / SLOT_MINUTES) *
    SLOT_MINUTES;
  const time = date.startOf('day').set({
    hour: Math.floor(slot / 60),
    minute: slot % 60,
  });
  const clock = time.toFormat(time.minute === 0 ? 'ha' : 'h:mma').toLowerCase();
  return `On ${date.toFormat('MMMM d, yyyy')} at ${clock}${columnClause(mode, column)}: `;
};
